import React, { useState, useEffect } from "react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { User, Mail, Shield, Camera, Edit2, CheckCircle, Wallet, TrendingUp, Zap, Save, X, Loader2, Phone, MessageCircle } from "lucide-react";
import { useAuth } from "../hooks/useAuth";
import walletService from "../services/walletService";
import api from "../services/api";
import { useTranslation } from "react-i18next";

export default function Profile() {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const [walletData, setWalletData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [successMsg, setSuccessMsg] = useState("");
  const [errorMsg, setErrorMsg] = useState("");
  const [editForm, setEditForm] = useState({
    username: user?.username || "",
    phone_number: user?.phone_number || "",
    telegram_username: user?.telegram_username || ""
  });

  useEffect(() => {
    const loadData = async () => {
      try {
        const [wallets, stats] = await Promise.all([
          walletService.getWallets(),
          walletService.getWalletStats()
        ]);
        setWalletData({ wallets, stats });
      } catch (err) {
        console.error("Error loading wallet data:", err);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, []);

  const handleSave = async () => {
    try {
      setSaving(true);
      setErrorMsg("");
      await api.put("/auth/profile", {
        phone_number: editForm.phone_number,
        telegram_username: editForm.telegram_username
      });
      setSuccessMsg(t('profile.profile_updated'));
      setIsEditing(false);
      setTimeout(() => setSuccessMsg(""), 3000);
    } catch (err) {
      setErrorMsg(t('profile.update_error'));
    } finally {
      setSaving(false);
    }
  };

  const totalBalance = walletData?.stats?.total_balance || 0;

  const memberSince = user?.created_at
    ? new Date(user.created_at).toLocaleDateString(i18n.language, { month: "long", year: "numeric" })
    : "—";

  return (
    <div className="min-h-screen bg-slate-950 text-white font-inter">
      <Header />

      {/* Background Ambience */}
      <div className="fixed top-0 left-0 w-full h-[600px] bg-gradient-to-b from-indigo-500/5 to-transparent pointer-events-none" />

      <main className="relative max-w-7xl mx-auto px-6 lg:px-8 py-32">

        {/* Success / Error Alerts */}
        {successMsg && (
          <div className="mb-6 px-6 py-4 bg-emerald-500/10 border border-emerald-500/30 rounded-2xl text-emerald-400 font-bold text-sm flex items-center gap-3">
            <CheckCircle size={18} /> {successMsg}
          </div>
        )}
        {errorMsg && (
          <div className="mb-6 px-6 py-4 bg-rose-500/10 border border-rose-500/30 rounded-2xl text-rose-400 font-bold text-sm">
            {errorMsg}
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">

          {/* Left Column: Avatar & Basic Info */}
          <div className="lg:col-span-4 space-y-8">
            <div className="glass-card p-10 border-white/5 relative group overflow-hidden shadow-2xl">
              <div className="absolute top-0 left-0 w-full h-32 bg-gradient-to-br from-indigo-600 to-cyan-500 opacity-20" />
              <div className="relative z-10 flex flex-col items-center pt-8">
                <div className="relative mb-6">
                  <div className="w-32 h-32 rounded-[2rem] bg-slate-900 border-4 border-slate-950 flex items-center justify-center text-4xl font-black shadow-2xl overflow-hidden group/avatar">
                    <div className="absolute inset-0 bg-gradient-to-br from-indigo-500 to-cyan-500 opacity-20" />
                    <span className="relative text-white">{user?.username?.charAt(0).toUpperCase() || "N"}</span>
                    <button className="absolute inset-0 bg-black/60 opacity-0 group-hover/avatar:opacity-100 transition-opacity flex items-center justify-center text-white">
                      <Camera size={24} />
                    </button>
                  </div>
                  <div className="absolute -bottom-2 -right-2 p-2 bg-emerald-500 rounded-xl border-4 border-slate-950 text-white shadow-lg">
                    <CheckCircle size={16} />
                  </div>
                </div>

                <h2 className="text-3xl font-black font-outfit uppercase tracking-tight mb-1 text-white">{user?.username || t('profile.user')}</h2>
                <p className="text-sm font-black text-slate-500 uppercase tracking-widest mb-6">{user?.role || t('profile.trader')}</p>

                <div className="flex gap-2 w-full">
                  {!isEditing ? (
                    <button
                      onClick={() => setIsEditing(true)}
                      className="flex-1 px-4 py-3 bg-white/5 rounded-xl border border-white/5 text-[10px] font-black text-slate-400 uppercase tracking-widest hover:bg-white/10 transition-all flex items-center justify-center gap-2"
                    >
                      <Edit2 size={12} /> {t('profile.edit_profile')}
                    </button>
                  ) : (
                    <div className="flex gap-2 w-full">
                      <button
                        onClick={handleSave}
                        disabled={saving}
                        className="flex-1 px-4 py-3 bg-indigo-500/20 border border-indigo-500/40 rounded-xl text-[10px] font-black text-indigo-400 uppercase tracking-widest hover:bg-indigo-500/30 transition-all flex items-center justify-center gap-2"
                      >
                        {saving ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />}
                        {t('profile.save')}
                      </button>
                      <button
                        onClick={() => setIsEditing(false)}
                        className="px-4 py-3 bg-white/5 rounded-xl border border-white/5 text-slate-500 hover:text-white transition-all"
                      >
                        <X size={14} />
                      </button>
                    </div>
                  )}
                </div>
              </div>

              <div className="mt-12 pt-10 border-t border-white/5 space-y-6">
                <div className="flex items-center justify-between text-xs font-bold">
                  <span className="text-slate-500 uppercase tracking-widest">{t('profile.member_since')}</span>
                  <span className="text-white capitalize">{memberSince}</span>
                </div>
                <div className="flex items-center justify-between text-xs font-bold">
                  <span className="text-slate-500 uppercase tracking-widest">{t('profile.kyc_status')}</span>
                  <span className={`font-black uppercase ${
                    user?.kyc_status === 'verified' ? 'text-emerald-400' :
                    user?.kyc_status === 'pending' ? 'text-amber-400' : 'text-slate-500'
                  }`}>
                    {user?.kyc_status === 'verified' ? t('profile.verified') :
                     user?.kyc_status === 'pending' ? t('profile.pending') : t('profile.not_verified')}
                  </span>
                </div>
                <div className="flex items-center justify-between text-xs font-bold">
                  <span className="text-slate-500 uppercase tracking-widest">{t('profile.email')}</span>
                  <span className="text-white text-right truncate max-w-[160px]">{user?.email || "—"}</span>
                </div>
              </div>
            </div>

            {/* Wallet Balance Card */}
            <div className="glass-card p-8 border-indigo-500/10 bg-gradient-to-br from-indigo-500/5 to-transparent">
              <div className="flex items-center gap-4 mb-6">
                <div className="p-3 bg-indigo-500/10 rounded-xl text-indigo-400">
                  <Wallet size={20} />
                </div>
                <span className="text-[10px] font-black text-slate-500 uppercase tracking-[0.2em]">{t('wallet.total_balance')}</span>
              </div>
              {loading ? (
                <div className="flex items-center gap-3">
                  <Loader2 size={20} className="animate-spin text-indigo-400" />
                  <span className="text-slate-500 text-sm font-bold">{t('common.loading')}</span>
                </div>
              ) : (
                <>
                  <div className="text-3xl font-black font-outfit text-white tracking-widest mb-2">
                    ${totalBalance.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </div>
                  <p className="text-[10px] font-black text-slate-500 tracking-widest uppercase">{t('profile.all_wallets')}</p>
                </>
              )}
            </div>
          </div>

          {/* Right Column: Account Details */}
          <div className="lg:col-span-8 space-y-8">
            <div className="glass-card p-10 border-white/5 shadow-2xl relative overflow-hidden">
              <div className="flex items-center justify-between mb-12">
                <div className="space-y-1">
                  <h3 className="text-2xl font-black font-outfit uppercase tracking-tight">{t('profile.title').split(' ').slice(0, -1).join(' ')} <span className="text-indigo-400">{t('profile.title').split(' ').slice(-1)}</span></h3>
                  <p className="text-xs font-bold text-slate-500">{t('profile.subtitle')}</p>
                </div>
                <Shield size={32} className="text-slate-800 opacity-20" />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
                {/* Email */}
                <div className="space-y-3">
                  <label className="text-[10px] font-black text-slate-500 uppercase tracking-[0.2em] ml-1">{t('auth.email_pro')}</label>
                  <div className="relative group">
                    <Mail className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-600" size={18} />
                    <input
                      type="text"
                      readOnly
                      value={user?.email || "—"}
                      className="w-full bg-slate-900/50 border border-white/5 rounded-2xl py-4 pl-12 pr-4 text-sm font-bold text-slate-400 cursor-not-allowed"
                    />
                  </div>
                </div>

                {/* ID */}
                <div className="space-y-3">
                  <label className="text-[10px] font-black text-slate-500 uppercase tracking-[0.2em] ml-1">{t('profile.nova_id')}</label>
                  <div className="relative group">
                    <User className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-600" size={18} />
                    <input
                      type="text"
                      readOnly
                      value={user?.id ? `NV-${String(user.id).padStart(6, "0")}` : "—"}
                      className="w-full bg-slate-900/50 border border-white/5 rounded-2xl py-4 pl-12 pr-4 text-sm font-bold text-slate-400 cursor-not-allowed uppercase"
                    />
                  </div>
                </div>

                {/* Phone */}
                <div className="space-y-3">
                  <label className="text-[10px] font-black text-slate-500 uppercase tracking-[0.2em] ml-1">{t('auth.phone_number') || 'Phone'}</label>
                  <div className="relative group">
                    <Phone className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-600" size={18} />
                    <input
                      type="text"
                      disabled={!isEditing}
                      value={editForm.phone_number}
                      onChange={e => setEditForm(f => ({ ...f, phone_number: e.target.value }))}
                      className={`w-full bg-slate-900 border border-white/10 rounded-2xl py-4 pl-12 pr-4 text-sm font-bold text-white outline-none transition-all ${isEditing ? 'focus:border-indigo-500/50' : 'opacity-70 cursor-not-allowed'}`}
                    />
                  </div>
                </div>

                {/* Telegram */}
                <div className="space-y-3">
                  <label className="text-[10px] font-black text-slate-500 uppercase tracking-[0.2em] ml-1">Telegram</label>
                  <div className="relative group">
                    <MessageCircle className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-600" size={18} />
                    <input
                      type="text"
                      disabled={!isEditing}
                      value={editForm.telegram_username}
                      onChange={e => setEditForm(f => ({ ...f, telegram_username: e.target.value }))}
                      className={`w-full bg-slate-900 border border-white/10 rounded-2xl py-4 pl-12 pr-4 text-sm font-bold text-white outline-none transition-all ${isEditing ? 'focus:border-indigo-500/50' : 'opacity-70 cursor-not-allowed'}`}
                    />
                  </div>
                </div>


              </div>

              <div className="mt-16 p-8 bg-slate-900/50 rounded-3xl border border-white/5 flex items-center justify-between">
                <div className="flex items-center gap-6">
                  <div className="p-3 bg-indigo-500/10 rounded-2xl text-indigo-400">
                    <Zap size={24} />
                  </div>
                  <div>
                    <h4 className="text-lg font-black font-outfit uppercase tracking-tight">{t('profile.security_verification')}</h4>
                    <p className="text-xs font-bold text-slate-500">{t('profile.security_desc')}</p>
                  </div>
                </div>
                <button className="px-6 py-3 rounded-xl border border-white/10 text-[10px] font-black text-slate-400 uppercase tracking-widest hover:text-white hover:border-white/20 transition-all">
                  {t('profile.manage_2fa')}
                </button>
              </div>
            </div>

            {/* Performance Snapshot */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="glass-card p-10 border-white/5 group hover:border-emerald-500/20 transition-all">
                <TrendingUp className="text-emerald-500 mb-6 group-hover:scale-110 transition-transform" size={32} />
                <h4 className="text-xl font-black font-outfit uppercase mb-2 tracking-tight">{t('profile.performance_trading')}</h4>
                <p className="text-slate-500 font-medium text-sm leading-relaxed mb-6">{t('profile.perf_desc')}</p>
                <span className="text-[10px] font-black text-emerald-400 uppercase tracking-widest flex items-center gap-2">
                  {t('wallet.total_balance')} : ${totalBalance.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </span>
              </div>
              <div className="glass-card p-10 border-white/5 group hover:border-cyan-500/20 transition-all">
                <CheckCircle className="text-cyan-500 mb-6 group-hover:scale-110 transition-transform" size={32} />
                <h4 className="text-xl font-black font-outfit uppercase mb-2 tracking-tight">{t('profile.account_status')}</h4>
                <p className="text-slate-500 font-medium text-sm leading-relaxed mb-6">{t('profile.account_status_desc')}</p>
                <span className={`text-[10px] font-black uppercase tracking-widest ${
                  user?.kyc_status === 'verified' ? 'text-emerald-400' :
                  user?.kyc_status === 'pending' ? 'text-amber-400' : 'text-slate-500'
                }`}>
                  {user?.kyc_status === 'verified' ? `✓ ${t('profile.verified')}` :
                   user?.kyc_status === 'pending' ? `⏳ ${t('profile.pending')}` : `⚠ ${t('profile.not_verified')}`}
                </span>
              </div>
            </div>
          </div>

        </div>
      </main>

      <Footer />
    </div>
  );
}
